import { post } from "@/shared/utils/fetchApi";
import type { MagicFillFormType, MagicFillMetadata } from "./MagicFillButton";

/**
 * Normalized result kept in camelCase so the calling components don't need to
 * know the wire shape.
 */
export interface MagicFillResult {
  suggestedFields?: Record<string, unknown>;
}

interface MagicFillRequestArgs {
  formType: MagicFillFormType;
  fields: Record<string, unknown>;
  metadata?: MagicFillMetadata;
  /** Arquivo real para extração/análise pelo LLM (multipart nos dois backends). */
  file?: File;
  signal: AbortSignal;
  language: string;
}

// Remove o objeto File de metadata.sourceFile antes de serializar — o File vai
// como campo separado no FormData; em JSON manda só name/type/size.
function metadataWithoutFile(metadata?: MagicFillMetadata): MagicFillMetadata {
  if (!metadata) return {};
  if (!metadata.sourceFile) return metadata;
  const { file: _file, ...rest } = metadata.sourceFile;
  return { ...metadata, sourceFile: rest };
}

// A api espera snake_case. `fieldOptions` nao tem contraparte e e descartado
// (vine ignora chaves desconhecidas). Valores vazios sao omitidos para o schema
// opcional do vine nao tropecar em undefined.
function toPayload(args: MagicFillRequestArgs) {
  const m = metadataWithoutFile(args.metadata);
  const metadata: Record<string, unknown> = {};
  if (m.organizationId) metadata.organization_id = m.organizationId;
  if (m.productId) metadata.product_id = m.productId;
  if (m.workspaceId) metadata.workspace_id = m.workspaceId;
  if (m.knowledgeBaseId) metadata.knowledge_base_id = m.knowledgeBaseId;
  if (m.fieldKey) metadata.field_key = m.fieldKey;
  if (m.fieldMetadata) metadata.field_metadata = m.fieldMetadata;
  if (m.onboarding !== undefined) metadata.onboarding = m.onboarding;
  if (m.sourceFile) metadata.source_file = m.sourceFile;
  return {
    form_type: args.formType,
    context: { fields: args.fields },
    metadata,
  };
}

/**
 * Envia o pedido de magic-fill e devolve o resultado normalizado em camelCase.
 * Falhas chegam como excecao ou como `suggestedFields` ausente.
 */
export async function requestMagicFill(
  args: MagicFillRequestArgs,
): Promise<MagicFillResult> {
  const headers = { "Accept-Language": args.language };
  const { signal, file } = args;

  if (file) {
    // FormData em camelCase: o controller coalesce `form_type ?? formType` e
    // normaliza as chaves do metadata.
    const formData = new FormData();
    formData.append("formType", args.formType);
    formData.append("context", JSON.stringify({ fields: args.fields }));
    formData.append(
      "metadata",
      JSON.stringify(metadataWithoutFile(args.metadata)),
    );
    formData.append("file", file);

    const res = await post<{ suggested_fields?: Record<string, unknown> }>(
      "/magic-fill",
      formData,
      { signal, headers },
    );
    return { suggestedFields: res?.suggested_fields };
  }

  const res = await post<{ suggested_fields?: Record<string, unknown> }>(
    "/magic-fill",
    toPayload(args),
    { signal, headers },
  );
  return { suggestedFields: res?.suggested_fields };
}
